import { lazy } from "react";
import { keyBy } from "lodash-es";
import {
  falTable,
  falBolt,
  fasChartBar,
  fasChartLine,
  fasTachometer,
  fasChartPie,
  fasPencilRuler,
  faSuperscript,
} from "../../styles/fontLibrary";
import {
  faSignal2,
  faTasksAlt as fasTasksAlt,
  faThList,
} from "@fortawesome/pro-solid-svg-icons";

const VerticalDivider = lazy(() =>
  import("../../charts/Dividers/VerticalDivider")
);
const JustLegend = lazy(() =>
  import("../../charts/MultipleLineChart/JustLegend")
);
const OKRCard = lazy(() => import("../../charts/OKRCard/OKRCard"));
const ProductStyleVisualization = lazy(() =>
  import("../../charts/ProductStyle/ProductStyleVisualization")
);
const WorldMapVisualization = lazy(() =>
  import("../../charts/WorldMap/WorldMapVisualization")
);
const ActiveTableVisualization = lazy(() =>
  import("../../charts/ActiveTable/ActiveTableVisualization")
);

const GroupedBarVisualization = lazy(() =>
  import("../../charts/GroupedBarVisualization")
);
const MultiGroupedBarVisualization = lazy(() =>
  import("../../charts/MultiGroupedBarVisualization")
);
const StackedBarTwoVisualization = lazy(() =>
  import("../../charts/StackedBar/StackedBarTwoVisualization")
);
const SalesFunnelVisualization = lazy(() =>
  import("../../charts/SalesFunnelVisualization")
);
const LineVisualization = lazy(() => import("../../charts/LineVisualization"));
const InfoCardGroup = lazy(() => import("../../charts/InfoCard/InfoCardGroup"));
const InfoCardTable = lazy(() =>
  import("../../charts/InfoCardTable/InfoCardTable")
);
const GaugeVisualization = lazy(() =>
  import("../../charts/GaugeVisualization")
);
const SummaryDetailPieVisualization = lazy(() =>
  import("../../charts/PieChart/SummaryDetailPieVisualization")
);
const ScatterVisualization = lazy(() =>
  import("../../charts/ScatterVisualization")
);
const TableParentVisualization = lazy(() =>
  import("../../charts/TableView/TableParentVisualization")
);
const GeoMapVisualization = lazy(() =>
  import("../../charts/GeoMapVisualization")
);
const WaterfallVisualization = lazy(() =>
  import("../../charts/Waterfall/WaterfallVisualization")
);
const LineBarChartVisualization = lazy(() =>
  import("../../charts/LineBarChart/LineBarChartVisualization")
);
const ChartDivider = lazy(() => import("../../charts/Dividers/ChartDivider"));

/**
 * @typedef {Object} ChartType
 * @property {string} key
 * @property {string} name Display name of the chart type
 * @property {React.Component|null} Component
 * @property {?import("@fortawesome/fontawesome-common-types").IconDefinition} icon
 * @property {?boolean} noQuery No query is to be associated with this chart e.g. dividers.
 */

export const chartSectionsLimit = 10;

export const chartTypeKeyMap = {
  GroupedBar: "GroupedBar",
  MultiBar: "MultiBar",
  StackedBars: "StackedBars",
  HorizontalBars: "HorizontalBars",
  LineChart: "LineChart",
  InfoCards: "Info Cards",
  InfoCardsTable: "Info Cards Table",
  OKRCard: "OKR Card",
  GaugeChart: "Gauge Chart",
  SummaryDetailPie: "SummaryDetailPie",
  Scatter: "Scatter",
  TableChart: "TableChart",
  GeoMap: "GeoMap",
  Waterfall: "Waterfall",
  LineBarChart: "LineBarChart",
  Divider: "Divider",
  VerticalDivider: "Vertical Divider",
  Legend: "Legend",
  ProductStyle: "ProductStyle",
  WorldMap: "WorldMap",
  ActiveTable: "ActiveTable",
};

/**
 * @typedef {Array} ChartTypeArray
 * @type {ChartType[]}
 */
export const chartTypes = Object.freeze([
  {
    key: chartTypeKeyMap.GroupedBar,
    Component: GroupedBarVisualization,
    name: "Target Bar",
    icon: faSignal2,
  },
  {
    key: chartTypeKeyMap.MultiBar,
    name: "Bar Chart",
    icon: fasChartBar,
    Component: MultiGroupedBarVisualization,
  },
  {
    key: chartTypeKeyMap.StackedBars,
    name: "Stacked Bar Chart",
    icon: fasTasksAlt,
    iconProps: {
      rotation: 270,
    },
    Component: StackedBarTwoVisualization,
  },
  {
    key: chartTypeKeyMap.HorizontalBars,
    Component: SalesFunnelVisualization,
  },
  {
    key: chartTypeKeyMap.LineChart,
    name: "Line Chart",
    icon: fasChartLine,
    Component: LineVisualization,
  },
  {
    key: chartTypeKeyMap.InfoCards,
    name: "Info Cards",
    icon: faSuperscript,
    Component: InfoCardGroup,
  },
  {
    key: chartTypeKeyMap.InfoCardsTable,
    name: "Info Cards Table",
    icon: faThList,
    Component: InfoCardTable,
  },
  {
    key: chartTypeKeyMap.OKRCard,
    Component: OKRCard,
  },
  {
    key: chartTypeKeyMap.GaugeChart,
    icon: fasTachometer,
    Component: GaugeVisualization,
  },
  {
    key: chartTypeKeyMap.SummaryDetailPie,
    icon: fasChartPie,
    name: "Pie Chart",
    Component: SummaryDetailPieVisualization,
  },
  {
    // Not in use
    key: chartTypeKeyMap.Scatter,
    Component: ScatterVisualization,
  },
  {
    key: chartTypeKeyMap.TableChart,
    name: "Table Chart",
    icon: falTable,
    Component: TableParentVisualization,
  },
  {
    key: chartTypeKeyMap.GeoMap,
    Component: GeoMapVisualization,
  },
  {
    key: chartTypeKeyMap.Waterfall,
    Component: WaterfallVisualization,
  },
  {
    key: chartTypeKeyMap.LineBarChart,
    Component: LineBarChartVisualization,
  },
  {
    key: chartTypeKeyMap.Divider,
    icon: fasPencilRuler,
    Component: ChartDivider,
    noQuery: true,
  },
  {
    key: chartTypeKeyMap.VerticalDivider,
    icon: fasPencilRuler,
    Component: VerticalDivider,
    noQuery: true,
  },
  {
    key: chartTypeKeyMap.Legend,
    Component: JustLegend,
  },
  {
    key: chartTypeKeyMap.ProductStyle,
    Component: ProductStyleVisualization,
  },
  {
    key: chartTypeKeyMap.WorldMap,
    Component: WorldMapVisualization,
  },
  {
    key: chartTypeKeyMap.ActiveTable,
    Component: ActiveTableVisualization,
    icon: falBolt,
  },
]);

/**
 * @type {Record<ChartTypeArray[number]['key'], ChartType>}
 */
export const chartTypeMap = keyBy(chartTypes, "key");

export function isInfoCardsChartType(type) {
  return (
    type === chartTypeKeyMap.InfoCards ||
    type === chartTypeKeyMap.InfoCardsTable
  );
}
